import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button, Field, Input, Lookup, Modal, Notice } from '../design/index.js'
import { useApi, type DocumentDetail } from '../app/api.js'
import { messageFor } from './feedback.js'

/**
 * `Change Currency` and `Change Tax Unit` (docs/06 §4 `Action ▾`).
 *
 * Both restate an approved document rather than reopening it. That distinction
 * is the whole point: reopening writes a revision and says the agreement
 * changed, and a rate settled after the order went out — or a GSTIN corrected
 * before filing — is the same agreement written down properly. The document
 * keeps its number and its approval.
 *
 * Changing the currency recomputes every line at the new rate, because the base
 * figures are what the reports add up.
 */
export interface RestateDialogProps {
  readonly docKey: string
  readonly doc: DocumentDetail
  readonly what: 'changeCurrency' | 'changeTaxUnit'
  readonly onClose: () => void
  readonly onDone: (saved: DocumentDetail) => void
}

export const RestateDialog = ({ docKey, doc, what, onClose, onDone }: RestateDialogProps) => {
  const api = useApi()
  const currency = what === 'changeCurrency'

  const [currencyId, setCurrencyId] = useState('')
  const [forexRate, setForexRate] = useState(doc.currency.forexRate)
  const [taxUnitId, setTaxUnitId] = useState(
    typeof doc.fields['taxUnitId'] === 'string' ? doc.fields['taxUnitId'] : '',
  )
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => api.restate(docKey, doc.id, currency
      ? { currencyId: currencyId || null, forexRate }
      : { taxUnitId: taxUnitId || null }),
    onSuccess: onDone,
    onError: (err) => setError(messageFor(err)),
  })

  return (
    <Modal
      title={currency ? 'Change the currency' : 'Change the tax unit'}
      subtitle={
        currency
          ? 'Every line is recomputed at the new rate. The document keeps its number and its approval.'
          : 'Which GSTIN this document is filed under. It keeps its number and its approval.'
      }
      tone="warn"
      actions={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button
            kind="primary"
            disabled={save.isPending || (currency ? forexRate.trim() === '' : taxUnitId === '')}
            onClick={() => save.mutate()}
          >
            {currency ? 'Restate it' : 'File it under this unit'}
          </Button>
        </>
      }
      onClose={onClose}
    >
      {error ? <Notice kind="bad">{error}</Notice> : null}

      {currency ? (
        <>
          <p className="helper">
            Currently {doc.currency.code} at {doc.currency.forexRate}.
          </p>
          <Field label="Currency" hint="Leave it empty for the currency this company keeps its books in.">
            <Lookup entity="currency" value={currencyId} onChange={setCurrencyId} />
          </Field>
          <Field
            label="Rate against the base currency"
            required
            hint="What one unit of that currency is worth. Only used for a foreign currency."
          >
            <Input value={forexRate} onChange={setForexRate} align="right" />
          </Field>
        </>
      ) : (
        <Field label="Tax unit" required hint="The GSTIN this document is filed under.">
          <Lookup entity="tax_unit" value={taxUnitId} onChange={setTaxUnitId} />
        </Field>
      )}
    </Modal>
  )
}
